import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Grid, Tab, Tabs } from '@mui/material';
import { useHistory, useLocation } from 'react-router';
import routes from 'store/configs/Routes';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import OrderHistoryPageView from 'components/payments/OrderHistoryPageView';
import MakePaymentPageView from 'components/payments/MakePaymentPageView';
import { MembershipContext } from 'components/MembershipGuard';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { ConfigContext } from 'components/ConfigGuard';
import { defaultGridContainerProps, defaultGridItemProps, getPageTitle } from 'util/Layout';
import PaymentService from 'services/api/PaymentService';
import { OrderPayment } from 'store/types/Payment';
import useRequest from 'hooks/useRequest';
import SiteModule from 'store/enums/SiteModule';
import DownloadStatementModal from 'components/payments/DownloadStatementModal';
import { CompanyPaymentDetails } from 'store/types/Company';
import ApplyCreditsToggle from 'components/payments/ApplyCreditsToggle';
import CompanyService from 'services/api/CompanyService';
import { ParametersContext } from 'components/ParametersGuard';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';
import { TabItem } from 'store/types/Tabs';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  BillingHistory: { key: 'BillingHistory', label: 'Billing History', route: routes.personalPayments } as TabItem,
  MakePersonalPayment: {
    key: 'MakePersonalPayment',
    label: 'Pay My Invoices',
    route: routes.makePersonalPayment,
  } as TabItem,
  MakePersonalSalesPayment: {
    key: 'MakePersonalSalesPayment',
    label: 'Pay My Sales Orders',
    route: routes.makePersonalSalesPayment,
  } as TabItem,
};

const PaymentsPage: React.FunctionComponent = () => {
  const { membershipLoading = false } = useContext(MembershipContext);
  const {
    isNccerTheme,
    isSmacnaTheme,
    sidebar,
    modulesSettings: {
      personalPayments: { enablePayInvoices, enablePaySalesOrders, hiddenTabs },
    },
  } = useContext(ConfigContext);
  const { userLoading = false, id } = useContext(UserContext);
  const { personalPayments } = useContext(ParametersContext);
  const location = useLocation();
  const [downloadStatementModalOpen, setDownloadStatementModalOpen] = useState<boolean>(false);
  const history = useHistory();
  const ordersRequestData = useRequest<OrderPayment[]>(PaymentService.getOrdersList);
  const activeTab: string = useMemo(() => getTabKeyByRoute(location.pathname, tabConfig), [location.pathname]);
  const companyDetailsRequest = useCallback(() => CompanyService.getCompanyPaymentDetails(id), [id]);
  const companyDetailsRequestData = useRequest<CompanyPaymentDetails>(companyDetailsRequest);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleDownloadStatementModalOpen = useCallback(() => {
    setDownloadStatementModalOpen(true);
  }, []);

  const handleDownloadStatementModalClose = useCallback(() => {
    setDownloadStatementModalOpen(false);
  }, []);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.BillingHistory.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, location.pathname, visibleTabs]);

  const generalStatementButton = useMemo(
    () => (
      <Grid {...defaultGridItemProps} xs={'auto'}>
        <Button variant={'contained'} color={'secondary'} onClick={handleDownloadStatementModalOpen}>
          {'Generate Statement'}
        </Button>
      </Grid>
    ),
    [handleDownloadStatementModalOpen]
  );

  const applyCreditsToggle = useMemo(
    () => (
      <Grid {...defaultGridItemProps} xs={'auto'}>
        <ApplyCreditsToggle id={id} {...companyDetailsRequestData} />
      </Grid>
    ),
    [companyDetailsRequestData, id]
  );

  return (
    <>
      {enablePayInvoices && downloadStatementModalOpen && (
        <DownloadStatementModal
          {...companyDetailsRequestData}
          open={downloadStatementModalOpen}
          onClose={handleDownloadStatementModalClose}
          id={id}
        />
      )}
      <h1 className={commonStyles.pageTitle}>
        {getPageTitle(sidebar, SiteModule.Payments, SiteModule.PersonalPayments, !isSmacnaTheme) ||
          'Billing & Invoices'}
      </h1>
      <TabContext value={activeTab}>
        {enablePayInvoices && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor={'primary'}
            textColor={'primary'}
            variant={'scrollable'}
          >
            {visibleTabs.map((tab) => {
              if (tab.key === tabConfig.MakePersonalPayment.key && !enablePayInvoices) {
                return;
              }

              if (tab.key === tabConfig.MakePersonalSalesPayment.key && !enablePaySalesOrders) {
                return;
              }

              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  className={commonStyles.tab}
                  classes={{ selected: commonStyles.activeTab }}
                />
              );
            })}
          </Tabs>
        )}
        <TabPanel className={commonStyles.tabPanel} value={tabConfig.BillingHistory.key}>
          <Spinner loading={userLoading || membershipLoading} transparent={false}>
            <Grid {...defaultGridContainerProps} justifyContent={'flex-end'}>
              {enablePayInvoices && (
                <>
                  {personalPayments.billingHistoryTabHeader && (
                    <Grid {...defaultGridItemProps}>
                      <p dangerouslySetInnerHTML={{ __html: personalPayments.billingHistoryTabHeader }} />
                    </Grid>
                  )}
                  {applyCreditsToggle}
                  {generalStatementButton}
                </>
              )}
              <Grid {...defaultGridItemProps}>
                <OrderHistoryPageView
                  {...ordersRequestData}
                  companyColumnEnabled={isNccerTheme}
                  openOrdersEnabled={!!enablePayInvoices}
                />
              </Grid>
            </Grid>
          </Spinner>
        </TabPanel>
        <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakePersonalPayment.key}>
          <Spinner loading={userLoading} transparent={false}>
            <Grid {...defaultGridContainerProps} justifyContent={'flex-end'} direction={'row'}>
              {
                <Grid {...defaultGridItemProps}>
                  {personalPayments.payMyInvoicesTabHeader && (
                    <p dangerouslySetInnerHTML={{ __html: personalPayments.payMyInvoicesTabHeader }} />
                  )}
                </Grid>
              }
              {generalStatementButton}
              <Grid {...defaultGridItemProps}>
                <MakePaymentPageView {...ordersRequestData} />
              </Grid>
            </Grid>
          </Spinner>
        </TabPanel>
        <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakePersonalSalesPayment.key}>
          <Spinner loading={userLoading} transparent={false}>
            <Grid {...defaultGridContainerProps} justifyContent={'flex-end'} direction={'row'}>
              {
                <Grid {...defaultGridItemProps}>
                  {personalPayments.payMySalesOrdersTabHeader && (
                    <p dangerouslySetInnerHTML={{ __html: personalPayments.payMySalesOrdersTabHeader }} />
                  )}
                </Grid>
              }
              {generalStatementButton}
              <Grid {...defaultGridItemProps}>
                <MakePaymentPageView {...ordersRequestData} salesOrders={true} />
              </Grid>
            </Grid>
          </Spinner>
        </TabPanel>
      </TabContext>
    </>
  );
};

export default PaymentsPage;
